import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import axios from 'axios';
import { showToast } from '../../utils/toastUtils';
import { useTheme } from '@mui/material/styles';

const planLimits = {
  basic: 1500,
  standard: 6000,
  premium: 12000,
};

const UsageDetails = () => {
  const [usageData, setUsageData] = useState({
    totalMinutes: 0,
    totalCalls: 0,
  });
  const [planDetails, setPlanDetails] = useState({
    name: 'basic',
    price: 4999,
  });
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const userId = localStorage.getItem('userId');
        if (!userId) {
          throw new Error('User not found. Please log in again.');
          window.location.href = 'https://www.verbalyze.in/login';
        }

        const [usageResponse, planResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/billing/billing-analytics`, { params: { userId } }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/plan`, { params: { userId } })
        ]);

        if (usageResponse.data.success) {
          setUsageData({
            totalMinutes: usageResponse.data.data.totalMinutes,
            totalCalls: usageResponse.data.data.totalCalls
          });
        } else {
          setUsageData({ totalMinutes: 0, totalCalls: 0 });
        }

        setPlanDetails(planResponse.data.planDetails);
      } catch (error) {
        showToast('Failed to fetch usage details. Please try again later.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const getColorClass = (value, total, isRemaining = false, isTotalCalls = false) => {
    const percentage = (value / total) * 100;
    if (isRemaining) {
      if (value === 0) return 'text-red-500';
      if (percentage <= 30) return 'text-orange-500';
      return 'text-blue-500';
    } else {
      if (percentage >= 100) return 'text-red-500';
      if (percentage >= 70) return 'text-orange-500';
      return 'text-blue-500';
    }
  };

  const getTotalCallsColor = (minutesUsed, planLimit) => {
    const percentage = (minutesUsed / planLimit) * 100;
    if (percentage >= 100) return 'text-red-500';
    if (percentage >= 70) return 'text-orange-500';
    return 'text-blue-500';
  };

  const UsageCard = ({ title, value, total, isRemaining = false, isTotalCalls = false }) => {
    let colorClass;
    if (isTotalCalls) {
      colorClass = getTotalCallsColor(usageData.totalMinutes, planLimit);
    } else {
      colorClass = getColorClass(value, total, isRemaining);
    }
    const progressColor = colorClass.replace('text-', 'bg-');
    const progressWidth = isRemaining ? ((value || 0) / total) * 100 : Math.min(((value || 0) / total) * 100, 100);

    return (
      <motion.div variants={itemVariants}>
        <Card className={`shadow-lg hover:shadow-xl transition-shadow duration-300 h-full ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
          <CardHeader className="pb-2">
            <h3 className={`text-lg font-semibold text-gray-700 ${isDarkMode ? 'text-slate-200' : ''}`}>{title}</h3>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-baseline mb-4">
              <span className={`text-3xl font-bold ${colorClass}`}>{value != null ? value.toLocaleString() : 'N/A'}</span>
              <span className={`text-sm text-gray-500 ${isDarkMode ? 'text-slate-300' : ''}`}>
                {total > 0 ? `out of ${total.toLocaleString()}` : ''}
              </span>
            </div>
            {total > 0 && (
              <div className={` rounded-full h- bg-slate-300 ${isDarkMode ? 'bg-slate-300' : ''}`}>
                <div
                  className={`${progressColor} h-2 rounded-full`}
                  style={{ width: `${progressWidth}%` }}
                ></div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  const planLimit = planLimits[planDetails.name] || 1500;
  const remainingMinutes = Math.max(0, planLimit - usageData.totalMinutes);

  return (
    <div ref={ref}>
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
        transition={{ duration: 0.6 }}
        className={`text-2xl font-semibold mb-6 text-gray-800 ${isDarkMode ? 'text-slate-100' : ''}`}
      >
        Usage Details
      </motion.h2>
      
      {isLoading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center items-center h-64"
        >
          <div className="animate-spin rounded-full h-24 w-24 border-t-2 border-b-2 border-blue-500"></div>
        </motion.div>
      ) : (
        <>
          <motion.div
            variants={containerVariants}
            animate={controls}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6"
          >
            <UsageCard
              title="Minutes Used" 
              value={usageData.totalMinutes} 
              total={planLimit} 
            />
            <UsageCard 
              title="Remaining Minutes" 
              value={remainingMinutes} 
              total={planLimit} 
              isRemaining={true}
            />
            <UsageCard 
              title="Total Calls" 
              value={usageData.totalCalls} 
              total={0} 
              isTotalCalls={true}
            />
          </motion.div>
         {/*  <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg"
          >
            <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-2">Current Plan: {planDetails.name.charAt(0).toUpperCase() + planDetails.name.slice(1)}</h3>
            <p className="text-blue-600 dark:text-blue-400">Limit: {planLimit.toLocaleString()} minutes per month</p>
            <p className="text-blue-600 dark:text-blue-400">Price: ₹{planDetails.price.toLocaleString()}</p>
          </motion.div> */}
         {/*  <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-4 text-sm text-gray-600 dark:text-gray-400"
          >
            Last updated: {new Date().toLocaleString()}
          </motion.div> */}
        </>
      )}
    </div>
  );
};

export default UsageDetails;



